// Code generated by apic; DO NOT EDIT.
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, type ChangeEvent } from "react";
import type { RestEndpointMeta, AuthMeta } from "./types";
import type { ApicClient } from "../react/client";
import { HTTPError } from "../react/client";
import { getApiToken } from "../react/auth";

/** Maximum response body characters (UTF-16 code units) shown in the panel (4 096 chars). */
const TRUNCATE_CHARS = 4096;

/**
 * tryCanSend determines whether the Send button should be enabled and returns
 * an explanatory note when it is disabled.  Exported so the pure auth-gating
 * logic can be unit-tested without React rendering.
 *
 *   - Returns { enabled: false, note } when a client certificate is required
 *     (mtlsRequired or mode/composite contains "mtls", "cac", or "piv").
 *   - Returns { enabled: false, note } when an api_key endpoint is present
 *     (mode is "api_key", or every satisfiable non-cert leaf in the DNF is
 *     "api_key").  HMAC signing requires the raw secret and cannot be performed
 *     from a browser; this matches how McpPanel handles the api_key case.
 *   - Returns { enabled: false, note } when an identity token is required
 *     (jwt or cookie present in mode/composite) and no token is currently set.
 *   - Returns { enabled: true } otherwise (public route, or token is present).
 */
export function tryCanSend(auth: AuthMeta, hasToken: boolean): { enabled: boolean; note?: string } {
  // Check for client-certificate requirement — always disabled from browser.
  const certModes = ["mtls", "cac", "piv"];
  if (auth.mtlsRequired) {
    return {
      enabled: false,
      note: "Requires a client certificate; cannot be exercised from the browser.",
    };
  }
  // Check composite/dnf for any cert-mode term.
  const allTerms: string[] = [];
  if (auth.mode !== "") {
    allTerms.push(auth.mode);
  }
  if (auth.dnf.length > 0) {
    for (const group of auth.dnf) {
      for (const term of group) {
        allTerms.push(term);
      }
    }
  }
  const requiresCert = allTerms.some((t) => certModes.includes(t));
  if (requiresCert) {
    return {
      enabled: false,
      note: "Requires a client certificate; cannot be exercised from the browser.",
    };
  }

  // Check whether the only satisfiable auth mode is api_key.  HMAC signing
  // requires the raw api_key secret (X-Nonce + X-Timestamp + HMAC body digest),
  // which must never be exposed in a browser context.
  //
  // Disable when:
  //   (a) single-mode "api_key", OR
  //   (b) composite where every non-cert leaf term is "api_key" (i.e. the only
  //       browser-reachable option is api_key HMAC signing).
  const nonCertTerms = allTerms.filter((t) => !certModes.includes(t));
  const isApiKeyOnly =
    nonCertTerms.length > 0 &&
    nonCertTerms.every((t) => t === "api_key");
  if (isApiKeyOnly) {
    return {
      enabled: false,
      note: "api_key (HMAC) signing isn’t supported from the browser explorer — use the CLI/SDK or a Bearer/JWT token.",
    };
  }

  // Check whether an identity token is required but not set.
  const identityModes = ["jwt", "cookie"];
  const requiresIdentity = allTerms.some((t) => identityModes.includes(t));
  if (requiresIdentity && !hasToken) {
    return {
      enabled: false,
      note: "Sign in / provide a token to try this endpoint.",
    };
  }

  return { enabled: true };
}

/**
 * effectiveAuthMode picks a usable identity auth mode for requestRaw.
 *
 * - If auth.mode is non-empty, return it directly.
 * - If auth.mode is "" (composite route), flatten the DNF and return the first
 *   leaf that is NOT a cert mode (mtls/cac/piv). This covers composite routes
 *   such as "jwt && cookie" or "cookie || jwt" whose mode field is left blank.
 * - Fall back to "public" when no identity leaf can be found.
 *
 * Exported so it can be unit-tested without React rendering.
 */
export function effectiveAuthMode(auth: AuthMeta): string {
  if (auth.mode !== "") {
    return auth.mode;
  }
  const certModes = new Set(["mtls", "cac", "piv"]);
  for (const group of auth.dnf) {
    for (const leaf of group) {
      if (!certModes.has(leaf)) {
        return leaf;
      }
    }
  }
  return "public";
}

/** Whether the HTTP method carries a request body. */
function methodHasBody(method: string): boolean {
  const m = method.toUpperCase();
  return m === "POST" || m === "PUT" || m === "PATCH" || m === "DELETE";
}

/** Substitute {param} placeholders in a path with the provided values. */
function substitutePath(path: string, params: Record<string, string>): string {
  return path.replace(/\{([^}]+)\}/g, (_match, name: string) => {
    return params[name] !== undefined && params[name] !== "" ? (params[name] as string) : "{" + name + "}";
  });
}

/** Truncate a string to TRUNCATE_CHARS characters (UTF-16 code units) and append a note when cut. */
function truncate(s: string): string {
  if (s.length <= TRUNCATE_CHARS) {
    return s;
  }
  return s.slice(0, TRUNCATE_CHARS) + "\n… (truncated at 4 096 chars)";
}

/**
 * TryPanel provides a live "Try it" interface for a single REST endpoint.
 *
 * Props:
 *   endpoint  – the RestEndpointMeta describing the endpoint to exercise.
 *   client    – a ApicClient (provided by the parent via useExplorerClient).
 *   baseUrl   – optional base URL prefix (informational; the client owns the
 *               actual base URL).
 *   className – extra CSS class appended to the root element.
 *
 * Behaviour:
 *   - Renders one text input per path param.
 *   - For POST/PUT/PATCH/DELETE, renders a JSON body textarea.
 *   - The Send button is disabled (with an explanatory note) when:
 *       (a) the endpoint requires a client certificate (mTLS/CAC/PIV),
 *       (b) the endpoint only accepts api_key (HMAC) auth which cannot be
 *           performed from a browser, or
 *       (c) an identity token is required but none is set.
 *   - On Send, prefers the typed client method (endpoint.methodName) when
 *     present on the client; otherwise falls back to client.requestRaw.
 *   - When responseKind is "blob" or "arrayBuffer", shows a binary/streaming
 *     summary (byte count) rather than attempting to JSON-parse the body.
 *   - Renders a status chip + truncated pretty-printed response body.
 *   - Never renders or logs a token value.
 *
 * No web-global dependencies at module load time (RN-safe).
 */
export function TryPanel(props: {
  endpoint: RestEndpointMeta;
  client: ApicClient;
  baseUrl?: string;
  className?: string;
}): JSX.Element {
  const { endpoint, client, className } = props;
  const root = ["apic-ui-try-panel", className].filter(Boolean).join(" ");

  // Path-param values, keyed by param name.
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  // JSON body textarea value (only used for unsafe methods).
  const [bodyText, setBodyText] = useState<string>("");
  // Response rendering state.
  const [loading, setLoading] = useState<boolean>(false);
  const [statusCode, setStatusCode] = useState<number | null>(null);
  const [responseBody, setResponseBody] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Re-evaluate token presence on every render so the button reflects the
  // current ambient token without requiring a React state change.
  const hasToken = getApiToken() !== null;
  const canSendResult = tryCanSend(endpoint.auth, hasToken);

  function handleParamChange(name: string, value: string): void {
    setParamValues((prev) => Object.assign({}, prev, { [name]: value }));
  }

  async function handleSend(): Promise<void> {
    setLoading(true);
    setStatusCode(null);
    setResponseBody(null);
    setErrorMsg(null);

    // Parse the JSON body when provided.
    let bodyObj: Record<string, unknown> = {};
    if (methodHasBody(endpoint.method) && bodyText.trim() !== "") {
      try {
        const parsed = JSON.parse(bodyText) as unknown;
        if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
          bodyObj = parsed as Record<string, unknown>;
        }
      } catch {
        setErrorMsg("Invalid JSON body.");
        setLoading(false);
        return;
      }
    }

    try {
      // Prefer the typed client method when it exists on the client object.
      const methodKey = endpoint.methodName;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (typeof (client as any)[methodKey] === "function") {
        // Merge path params and body fields into a single request object.
        const reqObj: Record<string, unknown> = Object.assign({}, paramValues, bodyObj);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const result = await (client as any)[methodKey](reqObj) as unknown;
        setStatusCode(200);
        let pretty: string;
        try {
          pretty = truncate(JSON.stringify(result, null, 2));
        } catch {
          pretty = String(result);
        }
        setResponseBody(pretty);
      } else {
        // Fall back to requestRaw: build the pathParams array and body map.
        const ppArray: Array<readonly [string, string]> = endpoint.pathParams.map(
          (p) => [p, paramValues[p] ?? ""] as readonly [string, string],
        );
        const resp = await client.requestRaw(
          endpoint.method,
          substitutePath(endpoint.path, paramValues),
          effectiveAuthMode(endpoint.auth),
          methodHasBody(endpoint.method) ? "json" : "query",
          ppArray,
          Object.assign({}, paramValues, bodyObj),
        );
        setStatusCode(resp.status);
        // Binary/streaming responses: show a byte-count summary rather than
        // attempting to decode the body as UTF-8 text and JSON-parse it.
        if (endpoint.responseKind === "blob" || endpoint.responseKind === "arrayBuffer") {
          const buf = await resp.arrayBuffer();
          setResponseBody("binary response (" + buf.byteLength + " bytes)");
        } else {
          const text = await resp.text();
          let pretty: string;
          try {
            pretty = truncate(JSON.stringify(JSON.parse(text), null, 2));
          } catch {
            pretty = truncate(text);
          }
          setResponseBody(pretty);
        }
      }
    } catch (err: unknown) {
      if (err instanceof HTTPError) {
        setStatusCode(err.status);
        let errBody: string;
        try {
          errBody = truncate(JSON.stringify(err.body, null, 2));
        } catch {
          errBody = err.bodyText;
        }
        setResponseBody(errBody);
      } else if (err instanceof Error) {
        setErrorMsg(err.message);
      } else {
        setErrorMsg("An unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  }

  const isSuccess = statusCode !== null && statusCode >= 200 && statusCode < 300;
  const isError = statusCode !== null && statusCode >= 400;

  return (
    <div className={root}>
      <div className="apic-ui-try-panel-header">
        <span className="apic-ui-try-panel-title">{"Try it"}</span>
      </div>

      {/* Path param inputs */}
      {endpoint.pathParams.length > 0 && (
        <div className="apic-ui-try-panel-params">
          {endpoint.pathParams.map((param) => (
            <div key={param} className="apic-ui-try-panel-param">
              <label className="apic-ui-try-panel-param-label" htmlFor={"apic-ui-param-" + param}>
                {param}
              </label>
              <input
                id={"apic-ui-param-" + param}
                type="text"
                className="apic-ui-try-panel-param-input"
                placeholder={"<" + param + ">"}
                value={paramValues[param] ?? ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) => { handleParamChange(param, e.target.value); }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Body textarea for unsafe methods */}
      {methodHasBody(endpoint.method) && (
        <div className="apic-ui-try-panel-body">
          <label className="apic-ui-try-panel-body-label" htmlFor="apic-ui-try-panel-body-input">
            {"Request body (JSON)"}
          </label>
          <textarea
            id="apic-ui-try-panel-body-input"
            className="apic-ui-try-panel-body-textarea"
            rows={6}
            placeholder={"{}"}
            value={bodyText}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => { setBodyText(e.target.value); }}
          />
        </div>
      )}

      {/* Binary/streaming response informational note */}
      {(endpoint.responseKind === "blob" || endpoint.responseKind === "arrayBuffer") && (
        <div className="apic-ui-try-panel-binary-note">
          <span className="apic-ui-chip apic-ui-chip--response-kind">
            {endpoint.responseKind === "arrayBuffer" ? "streaming response" : "binary response"}
          </span>
          <span className="apic-ui-try-panel-note">
            {"Response is binary/streaming — the explorer will show a byte-count summary, not text."}
          </span>
        </div>
      )}

      {/* Send button + disabled note */}
      <div className="apic-ui-try-panel-actions">
        <button
          className={
            "apic-ui-try-panel-send" +
            (!canSendResult.enabled ? " apic-ui-try-panel-send--disabled" : "") +
            (loading ? " apic-ui-try-panel-send--loading" : "")
          }
          disabled={!canSendResult.enabled || loading}
          onClick={() => { void handleSend(); }}
        >
          {loading ? "Sending…" : "Send"}
        </button>
        {!canSendResult.enabled && canSendResult.note !== undefined && (
          <span className="apic-ui-try-panel-note">{canSendResult.note}</span>
        )}
      </div>

      {/* Error message */}
      {errorMsg !== null && (
        <div className="apic-ui-try-panel-error">
          <span className="apic-ui-try-panel-error-msg">{errorMsg}</span>
        </div>
      )}

      {/* Response display */}
      {statusCode !== null && (
        <div className="apic-ui-try-panel-response">
          <span
            className={
              "apic-ui-try-panel-status" +
              (isSuccess ? " apic-ui-try-panel-status--success" : "") +
              (isError ? " apic-ui-try-panel-status--error" : "")
            }
          >
            {String(statusCode)}
          </span>
          {responseBody !== null && (
            <pre className="apic-ui-try-panel-response-body">
              <code>{responseBody}</code>
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
